import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { PageHeader } from "@/components";
import { TabPanel } from "@/components/Tabs/Tabs";
import { useHasPermissions } from "@/utils/useHasPermissions";
import { PermissionRoles } from "@/enum";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { useMarkTemplatesReviewedMutation } from "@/pages/Private/redux/messageTemplate/messageTemplate.api";

import { Prompts } from "./pages/Prompts/Prompts";
import { Replies } from "./pages/Replies/Replies";
import { DeepResearchPrompts } from "./pages/DeepResearchPrompts/DeepResearchPrompts";

enum Tabs {
	PROMPTS = "PROMPTS",
	REPLIES = "REPLIES",
	DEEP_RESEARCH_PROMPTS = "DEEP_RESEARCH_PROMPTS",
}

export const Templates: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`templates.${key}`);
	const [searchParams, setSearchParams] = useSearchParams();

	const [markTemplatesReviewed] = useMarkTemplatesReviewedMutation();

	// Visiting the section counts as reviewing it — clears the left-nav orange dot.
	useEffect(() => {
		markTemplatesReviewed();
	}, [markTemplatesReviewed]);

	const [currentTab, setCurrentTab] = useState<Tabs>(
		(searchParams.get("tab") as Tabs) || Tabs.PROMPTS
	);

	useEffect(() => {
		if (searchParams.has("tab")) {
			const nextTab = searchParams.get("tab") as Tabs;

			setCurrentTab(nextTab);
		}
	}, [searchParams]);

	useEffect(() => {
		const nextTab = searchParams.has("tab") ? (searchParams.get("tab") as Tabs) : Tabs.PROMPTS;

		if (nextTab !== currentTab) {
			setSearchParams({ ...searchParams, tab: nextTab }, { replace: true });
		}
	}, [searchParams]);

	const { hasPermissions } = useHasPermissions();
	const isDeepResearchActive = useIsFeatureActive(FeaturesCodes.DEEP_RESEARCH);

	let tabs = [
		{ label: ts("tabs.prompts"), value: Tabs.PROMPTS, permission: PermissionRoles.TEMPLATES },
		{
			label: ts("tabs.replies"),
			value: Tabs.REPLIES,
			permission: PermissionRoles.TEMPLATES,
		},
		...(isDeepResearchActive
			? [
					{
						label: ts("tabs.deepResearchPrompts"),
						value: Tabs.DEEP_RESEARCH_PROMPTS,
						permission: PermissionRoles.DEEP_RESEARCH,
					},
				]
			: []),
	];

	tabs = tabs.filter((tab) => hasPermissions([tab.permission]));

	const handleTabsChange = (value: string): void => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);

				next.set("tab", value);

				return next;
			},
			{ replace: true }
		);
	};

	return (
		<div className="flex flex-col h-full w-full">
			<PageHeader
				activeTab={currentTab}
				tabs={tabs.map((tab) => ({ label: tab.label, value: tab.value }))}
				title={ts("title")}
				onTabChange={handleTabsChange}
			/>

			<TabPanel activeTab={currentTab} value={Tabs.PROMPTS}>
				<Prompts />
			</TabPanel>
			<TabPanel activeTab={currentTab} value={Tabs.REPLIES}>
				<Replies />
			</TabPanel>
			{isDeepResearchActive && (
				<TabPanel activeTab={currentTab} value={Tabs.DEEP_RESEARCH_PROMPTS}>
					<DeepResearchPrompts />
				</TabPanel>
			)}
		</div>
	);
};
